import type { FC } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { makeThreeLOPromptForCustomUI } from '@atlassian/forge-ui/components/threeLOPrompt';
import { ModalCustomUIRenderer } from '@atlassian/forge-ui/ui';
import type { Extension } from '@atlassian/forge-ui/web-client/graphql/types';
import type { InternalModalState } from '@atlassian/forge-ui/renderer/forge-ui-renderer/types';
import { resolveDynamicResource } from '@atlassian/forge-ui/utils/resolveDynamicResource';

import { fg } from '@confluence/feature-gating';
import { getBrowserHistory } from '@confluence/browser-history';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useGetContextToken } from '../hooks/useGetContextToken.tsx';
import { useWorkspaceARI } from '../hooks/useWorkspaceARI.tsx';
import { getMentionProvider } from '../utils/mentionProvider.ts';
import { getFlagProvider } from '../utils/flagProvider.tsx';
import type { EmbeddedForgeOpenModalBridgePayload } from '../hooks/useEmbeddedMacroListeners.ts';
import type { ForgeUIExtensionDataType } from '../types.ts';

/**
 * Public payload type re-exported for callers that store/forward the bridge
 * payload (e.g. `ADFRendererComponent`).
 */
export type EmbeddedForgeOpenModalPayload = EmbeddedForgeOpenModalBridgePayload;

const toInternalModalState = (raw: EmbeddedForgeOpenModalPayload): InternalModalState | null => {
	const extension = raw.extension as Extension | undefined;
	if (!extension) {
		return null;
	}

	const onClose =
		typeof raw.onClose === 'function' ? (raw.onClose as (payload?: unknown) => void) : undefined;

	return {
		resource: raw.resource ?? undefined,
		size: raw.size as InternalModalState['size'],
		context: raw.context,
		closeOnEscape: raw.closeOnEscape,
		closeOnOverlayClick: raw.closeOnOverlayClick,
		onClose,
		title: raw.title,
		icon: raw.icon,
		embeddedMacroLocalId: raw.embeddedMacroLocalId,
		extension,
		extensionData: raw.extensionData ?? undefined,
		extensionPayload: raw.extensionPayload ?? undefined,
		isEmbeddedMacroModal: Boolean(raw.isEmbeddedMacroModal),
	};
};

export type EmbeddedForgeMacroModalProps = {
	payload: EmbeddedForgeOpenModalPayload | null;
	flags: FlagsStateContainer;
	onDismiss: () => void;
};

export const EmbeddedForgeMacroModal: FC<EmbeddedForgeMacroModalProps> = ({
	payload,
	flags,
	onDismiss,
}) => {
	const canRenderModal = useMemo(() => {
		if (!payload) {
			return false;
		}
		const state = toInternalModalState(payload);
		return Boolean(state?.extension);
	}, [payload]);

	if (!payload || !canRenderModal) {
		return null;
	}

	return <EmbeddedForgeMacroModalInner payload={payload} flags={flags} onDismiss={onDismiss} />;
};

type InnerProps = {
	payload: EmbeddedForgeOpenModalPayload;
	flags: FlagsStateContainer;
	onDismiss: () => void;
};

const EmbeddedForgeMacroModalInner: FC<InnerProps> = ({ payload, flags, onDismiss }) => {
	const modalOptions = useMemo(() => toInternalModalState(payload), [payload]);

	const { cloudId, environment, userId } = useSessionData();
	const isLivePage = useIsCurrentPageLive();
	const { locale } = useIntl();
	const workspaceARI = useWorkspaceARI();
	const contextIds = useMemo(() => [workspaceARI], [workspaceARI]);
	const [isReducedMotion, setIsReducedMotion] = useState(false);
	useEffect(() => {
		if (typeof window === 'undefined') {
			return;
		}
		const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
		setIsReducedMotion(mq.matches);
		const onChange = () => setIsReducedMotion(mq.matches);
		mq.addEventListener('change', onChange);
		return () => mq.removeEventListener('change', onChange);
	}, []);

	const { showFlag, closeFlag } = getFlagProvider(flags);

	const baseExtension = modalOptions?.extension;

	const updatedExtensionData = useMemo(() => {
		const baseExtensionData = (modalOptions?.extensionData || {}) as ForgeUIExtensionDataType;
		return {
			...baseExtensionData,
			content: baseExtensionData.content
				? {
						...baseExtensionData.content,
						subtype: isLivePage ? 'live' : null,
					}
				: undefined,
		} as ForgeUIExtensionDataType;
	}, [isLivePage, modalOptions?.extensionData]);

	const getContextTokenHandler = useGetContextToken({
		extension: baseExtension as Extension,
		extensionData: updatedExtensionData,
		localId: modalOptions?.embeddedMacroLocalId,
		contextIds,
		cachingEnabled: true,
	});

	const [viewOnClose, setViewOnClose] = useState<(() => Promise<void> | void) | undefined>();

	const coreData = useMemo(() => {
		if (!baseExtension) {
			return undefined;
		}
		const { id: extensionId, environmentId, environmentType } = baseExtension;
		return {
			workspaceId: undefined as string | undefined,
			cloudId,
			localId: modalOptions?.embeddedMacroLocalId || extensionId,
			environmentId,
			environmentType,
		};
	}, [baseExtension, cloudId, modalOptions?.embeddedMacroLocalId]);

	const getThreeLOPrompt = useCallback(() => {
		return {
			ThreeLOPrompt: makeThreeLOPromptForCustomUI({
				appName: baseExtension?.properties?.title ?? '',
				onSuccess: () => {},
			}),
		};
	}, [baseExtension?.properties?.title]);

	const onCloseFromModalClass = modalOptions?.onClose;

	// Memoised so the object identity is stable across renders. Without this,
	// every render produces a fresh `{}` and any downstream useMemo that lists
	// `embeddedMacroModalProps` in its deps would invalidate on every render.
	const embeddedMacroModalProps = useMemo(
		() =>
			modalOptions?.isEmbeddedMacroModal && modalOptions && coreData
				? {
						extensionPayload: modalOptions.extensionPayload,
						extensionViewData: undefined,
						coreData: {
							...coreData,
							localId: modalOptions.embeddedMacroLocalId || '',
						},
					}
				: {},
		[coreData, modalOptions],
	);

	const iframeProps = useMemo(() => {
		if (!coreData || !baseExtension || !modalOptions) {
			return null;
		}
		return {
			accountId: userId || undefined,
			components: getThreeLOPrompt,
			contextIds,
			coreData,
			customBridgeMethods: undefined,
			extension: baseExtension,
			extensionData: updatedExtensionData,
			extensionPayload: modalOptions.extensionPayload,
			extensionViewData: undefined,
			getContextToken: getContextTokenHandler,
			isHidden: false,
			isResizable: false,
			locale,
			timezone: undefined as string | undefined,
			loadingComponent: undefined,
			isEmbeddedMacro: false,
			embeddedParentLocalId: undefined,
			embeddedMacroModalProps,
		};
	}, [
		baseExtension,
		contextIds,
		coreData,
		getContextTokenHandler,
		getThreeLOPrompt,
		locale,
		modalOptions,
		updatedExtensionData,
		userId,
		embeddedMacroModalProps,
	]);

	const modalIframeProps = useMemo(() => {
		if (!iframeProps) {
			return null;
		}
		return {
			...iframeProps,
			...embeddedMacroModalProps,
		};
	}, [embeddedMacroModalProps, iframeProps]);

	const extensionInModal = useMemo(() => {
		if (!modalOptions || !baseExtension) {
			return baseExtension as Extension;
		}
		// `openModal`'s resource is optional; only override when one is explicitly provided. When it is
		// omitted the modal defaults to the current resource, so we inherit it (and its entry) untouched.
		let resourceOverride: { resource?: string; resolvedResourceEntry?: string } = {};
		if (modalOptions?.resource) {
			resourceOverride = fg('forge-ui-multi-entry-resource-macro-config-fix')
				? resolveDynamicResource(modalOptions.resource)
				: { resource: modalOptions.resource };
		}
		return {
			...baseExtension,
			type: `${baseExtension.type}::dynamic-modal`,
			properties: {
				...baseExtension.properties,
				...resourceOverride,
			},
		};
	}, [baseExtension, modalOptions]);

	const extensionDataInModal = useMemo(() => {
		if (!modalOptions || !baseExtension) {
			return updatedExtensionData;
		}
		const base = (modalOptions.extensionData || updatedExtensionData) as ForgeUIExtensionDataType;
		return {
			...base,
			...(modalOptions.context ? { modal: modalOptions.context } : {}),
			...(baseExtension.type.startsWith('jiraServiceManagement') && modalOptions.context
				? { ...modalOptions.context }
				: {}),
		};
	}, [baseExtension, modalOptions, updatedExtensionData]);

	const handleModalTearDown = useCallback(() => {
		onDismiss();
	}, [onDismiss]);

	const handleModalClose = useCallback(() => {
		onCloseFromModalClass?.();
		if (isReducedMotion) {
			handleModalTearDown();
		}
	}, [handleModalTearDown, isReducedMotion, onCloseFromModalClass]);

	const bridge = useMemo(
		() => ({
			showFlag,
			closeFlag,
			history: getBrowserHistory(),
			onClose: (callback: { data?: () => Promise<void> | void }) => {
				setViewOnClose(() => callback.data);
				return true;
			},
			close: (closePayload: { data?: unknown }) => {
				onCloseFromModalClass?.(closePayload?.data);
				return true;
			},
		}),
		[closeFlag, onCloseFromModalClass, showFlag],
	);

	if (!modalOptions || !baseExtension || !iframeProps || !modalIframeProps) {
		return null;
	}

	return (
		<ModalCustomUIRenderer
			{...iframeProps}
			product="confluence"
			getThreeLOPrompt={getThreeLOPrompt}
			extension={extensionInModal}
			extensionData={extensionDataInModal}
			localId={modalOptions.embeddedMacroLocalId ?? baseExtension.id}
			onTearDown={handleModalTearDown}
			bridge={bridge}
			modalOnClose={handleModalClose}
			size={modalOptions.size}
			title={modalOptions.title}
			icon={modalOptions.icon}
			closeOnEscape={modalOptions.closeOnEscape}
			closeOnOverlayClick={modalOptions.closeOnOverlayClick}
			getModalWidth={undefined}
			isIframeResizable={false}
			viewOnClose={viewOnClose}
			mentionProvider={getMentionProvider(cloudId, environment, userId)}
		/>
	);
};
